import { useEffect, useState } from 'react';
import {
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { visitsApi, storesApi, Visit, VisitDetail, Store } from '../services/api';

const API_URL = import.meta.env.VITE_API_URL || '';

function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(dateString: string | undefined | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDateTime(dateString: string | undefined | null): string {
  if (!dateString) return '-';
  return `${formatDate(dateString)} at ${formatTime(dateString)}`;
}

function getScoreColor(score: number | undefined | null): string {
  if (score === undefined || score === null) return 'text-gray-500';
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  return 'text-red-600';
}

function getScoreBgColor(score: number | undefined | null): string {
  if (score === undefined || score === null) return 'bg-gray-100';
  if (score >= 90) return 'bg-green-100';
  if (score >= 70) return 'bg-yellow-100';
  return 'bg-red-100';
}

export default function Visits() {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<VisitDetail | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStore, setFilterStore] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const perPage = 20;

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    loadVisits();
  }, [page, filterStore]);

  async function loadStores() {
    try {
      const data = await storesApi.list();
      setStores(data);
    } catch (err) {
      console.error('Failed to load stores:', err);
    }
  }

  async function loadVisits() {
    setIsLoading(true);
    setError(null);
    try {
      const result = await visitsApi.list({
        page,
        per_page: perPage,
        status: 'completed',
        store_id: filterStore || undefined,
      });
      setVisits(result.data);
      setTotalPages(Math.ceil(result.meta.total / perPage));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load visits');
    } finally {
      setIsLoading(false);
    }
  }

  async function viewVisitDetail(visit: Visit) {
    setLoadingDetail(true);
    setShowModal(true);
    try {
      const detail = await visitsApi.get(visit.id);
      setSelectedVisit(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load visit details');
      setShowModal(false);
    } finally {
      setLoadingDetail(false);
    }
  }

  function closeModal() {
    setShowModal(false);
    setSelectedVisit(null);
  }

  if (isLoading && visits.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Visit Results</h1>
          <p className="mt-1 text-sm text-gray-500">
            View store visit history, scores, and details
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium ${
            showFilters || filterStore
              ? 'border-primary-500 text-primary-700 bg-primary-50'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          <FunnelIcon className="h-5 w-5 mr-2" />
          Filters
          {filterStore && (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-primary-500 text-white">1</span>
          )}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow-sm rounded-xl p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Store
              </label>
              <select
                value={filterStore}
                onChange={(e) => {
                  setFilterStore(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Stores</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
            {filterStore && (
              <button
                onClick={() => {
                  setFilterStore('');
                  setPage(1);
                }}
                className="mt-6 text-sm text-gray-500 hover:text-gray-700"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Visits Table */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Store
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visitor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visits.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No visits found
                </td>
              </tr>
            ) : (
              visits.map((visit) => (
                <tr key={visit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {visit.store_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        #{visit.store_number}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {visit.user_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(visit.completed_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTime(visit.completed_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getScoreBgColor(
                        visit.overall_score
                      )} ${getScoreColor(visit.overall_score)}`}
                    >
                      {visit.overall_score ?? '-'}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => viewVisitDetail(visit)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Visit Detail Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={closeModal}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Visit Details
                  </h2>
                  {selectedVisit && (
                    <p className="text-sm text-gray-500">
                      {selectedVisit.store_name} - {formatDateTime(selectedVisit.completed_at)}
                    </p>
                  )}
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingDetail ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                  </div>
                ) : selectedVisit ? (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-500">Store</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {selectedVisit.store_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          #{selectedVisit.store_number}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-500">Visitor</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {selectedVisit.user_name}
                        </div>
                      </div>
                      <div className={`rounded-lg p-4 ${getScoreBgColor(selectedVisit.overall_score)}`}>
                        <div className="text-sm text-gray-500">Overall Score</div>
                        <div className={`text-3xl font-bold ${getScoreColor(selectedVisit.overall_score)}`}>
                          {selectedVisit.overall_score ?? '-'}%
                        </div>
                      </div>
                    </div>

                    {/* Scores by Group */}
                    {selectedVisit.scores && selectedVisit.scores.filter(s => s.group_id).length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">
                          Scores by Category
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {selectedVisit.scores
                            .filter((s) => s.group_id)
                            .map((score) => (
                              <div
                                key={score.id}
                                className="bg-gray-50 rounded-lg p-3"
                              >
                                <div className="text-xs text-gray-500 truncate">
                                  {score.group_name || 'Unknown'}
                                </div>
                                <div className={`text-xl font-bold ${getScoreColor(score.score_percentage)}`}>
                                  {score.score_percentage}%
                                </div>
                                <div className="text-xs text-gray-400">
                                  {score.passed_questions}/{score.total_questions} passed
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Answers */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Question Responses ({selectedVisit.answers?.length || 0})
                      </h3>
                      <div className="space-y-2">
                        {selectedVisit.answers?.map((answer) => (
                          <div
                            key={answer.id}
                            className={`border rounded-lg p-3 ${
                              answer.passed === 1
                                ? 'border-green-200 bg-green-50'
                                : 'border-red-200 bg-red-50'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {answer.passed === 1 ? (
                                    <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                                  ) : (
                                    <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                                  )}
                                  <span className="text-sm font-medium text-gray-900">
                                    {answer.question_text}
                                  </span>
                                </div>
                                <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                                  <span>{answer.group_name}</span>
                                  {answer.severity && (
                                    <span className={`px-2 py-0.5 rounded-full ${
                                      answer.severity === 'critical'
                                        ? 'bg-red-100 text-red-700'
                                        : answer.severity === 'moderate'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {answer.severity}
                                    </span>
                                  )}
                                </div>
                                {answer.comment && (
                                  <div className="mt-2 text-sm text-gray-600 bg-white rounded p-2">
                                    {answer.comment}
                                  </div>
                                )}
                              </div>
                              {/* Photos */}
                              {answer.photos && answer.photos.length > 0 && (
                                <div className="flex gap-2 flex-shrink-0">
                                  {answer.photos.map((photo, idx) => (
                                    <a
                                      key={idx}
                                      href={`${API_URL}/api/photos/${photo.photo_url.split('/').pop()}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block w-16 h-16 rounded-lg overflow-hidden bg-gray-200 hover:opacity-75 transition-opacity"
                                    >
                                      <div className="w-full h-full flex items-center justify-center">
                                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                                      </div>
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-12">
                    Failed to load visit details
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
